// src/oura/components/docs/create/createModel.ts
//
// What Create a doc (B12, scope 2.2) and the Skill library (B13, 2.3) share:
// the 4 ways to make a doc, the name and page title a new doc gets in the
// the decided name (B16's rename format, previewed live), where a skill plugs in
// and what Oura would send it, and the cap placeholders.
//
// The skill path runs the doc type's skill once one is added (ourteam-backend#5268,
// #3983); until then it shows, shut, with where it plugs in. A run's cost reads
// "not measured" until it is, never 0.

import { useEffect, useState } from "react";
import { docNameOf, getSkillLibrary, lineTwoOf, type CreateDocKind, type DocTemplate, type SkillLibrary, type SpendCap } from "@service/aeo/clientDocsWrite";
import { codeChipsOf, joinCodes, type BusinessDocuments, type StaffDocument } from "@service/aeo/clientDocs";
import { DOC_EXPECTED, expectedDocFor, isMonthlyType, merchantDocName, monthPart, outletOk, termMonth } from "@service/aeo/clientDocsRules";
import type { DocToSend } from "../boardModel";

/** What a Create opens with: a doc to send from the board, a code row, or nothing (pick a type). */
export interface CreatePrefill {
    docType: string | null;
    /** Base codes, ["S1","S3"]; [] = the doc type's own. */
    codes: string[];
    outlet: string | null;
    /** How to make it; absent = from the template. */
    kind?: CreateDocKind;
    /** kind "convert": the Word file's Drive id, already picked. */
    fileId?: string | null;
    /** A custom doc type's name, already known: Create opens on the Custom row with it (ourteam-backend#6516). */
    customType?: string | null;
}

/** A Word file's "Convert to a Google Doc": Create on convert with the file picked, and what its name says (#4307). */
export function prefillFromWord(d: Pick<StaffDocument, "docType" | "codes" | "outlet" | "driveFileId" | "custom">): CreatePrefill {
    // A Word file named with a custom doc type ("(S1) <Client>_Treatment Menu_….docx", ourteam-backend#6516):
    // the Custom row with its name and its codes; the sheet keeps those in the plan.
    if (d.custom && d.docType) {
        return { docType: null, customType: d.docType, codes: [...d.codes], outlet: d.outlet, kind: "convert", fileId: d.driveFileId };
    }
    // Only the name's codes that are this doc type's: "(G1) …_Answer Plan_…" ticked no Answer Plan code and
    // left Create disabled. None left, and Create ticks the type's own, as with no prefill (#4311).
    const type = DOC_EXPECTED.find((e) => e.docType === d.docType);
    const own = type ? d.codes.filter((c) => type.codes.includes(c)) : [];
    return { docType: d.docType, codes: own, outlet: d.outlet, kind: "convert", fileId: d.driveFileId };
}

export const EMPTY_PREFILL: CreatePrefill = { docType: null, codes: [], outlet: null };

/** A doc to send, as Create's prefill: its type, the codes it clears and the outlet it is for. */
export function prefillFromToSend(d: Pick<DocToSend, "docType" | "rows">): CreatePrefill {
    const codes = Array.from(new Set(d.rows.map((r) => r.code)));
    return { docType: d.docType, codes, outlet: d.rows.find((r) => r.outlet)?.outlet ?? null };
}

/** A code row with no doc, as Create's prefill; null for a code no catalogue doc covers. */
export function prefillFromCode(code: string, outlet: string | null): CreatePrefill | null {
    const doc = expectedDocFor(code);
    return doc ? { docType: doc.docType, codes: [code], outlet: doc.perOutlet ? outlet : null } : null;
}

/** The 4 ways, in the order the sheet offers them. The skill path stays last and shut until a skill is added. */
export const CREATE_KINDS: readonly CreateDocKind[] = ["template", "link", "convert", "skill"];

export const KIND_LABEL: Record<CreateDocKind, string> = {
    template: "From the template",
    link: "From a Google Doc we have",
    convert: "From a Word file in the folder",
    skill: "Write it with the skill",
    blank: "A blank Google Doc",
};

/** A custom doc type's ways, when the backend's row does not list them (ourteam-backend#6517). */
export const CUSTOM_KINDS: readonly CreateDocKind[] = ["blank", "link", "convert"];

/** The custom doc types this client already uses, A to Z, for quick picks (ourteam-backend#6516). */
export function customLabelsOf(board: Pick<BusinessDocuments, "documents"> | null): string[] {
    const seen = new Map<string, string>();
    for (const d of board?.documents ?? []) {
        if (d.custom && d.docType && !seen.has(d.docType.toLowerCase())) seen.set(d.docType.toLowerCase(), d.docType);
    }
    return Array.from(seen.values()).sort((a, b) => a.localeCompare(b));
}

/** "Month 2": a code row's plan month, for the custom codes list; null when the plan does not say. */
export function planMonthText(month: number | null | undefined): string | null {
    return typeof month === "number" && month > 0 ? `Month ${month}` : null;
}

/** The Skill library, read once per page: Create shows the picked type's own inputs from it. */
let libraryRead: Promise<SkillLibrary | null> | null = null;

/**
 * What Oura would send the skill for this doc type, as its Skill library slot
 * lists it (B13): a skill runs with no internet, so each slot names its own
 * inputs. Null while it loads, or when the type has no slot.
 */
export function useSkillInputs(docType: string | null): string[] | null {
    const [library, setLibrary] = useState<SkillLibrary | null>(null);
    useEffect(() => {
        let live = true;
        libraryRead ??= getSkillLibrary().then((r) => (r.ok ? r.data : null));
        void libraryRead.then((l) => {
            if (live) setLibrary(l);
        });
        return () => {
            live = false;
        };
    }, []);
    const slot = docType ? library?.slots.find((s) => s.docType === docType) : null;
    return slot && slot.inputs.length > 0 ? slot.inputs.map((i) => i.label) : null;
}

/** "a, b and c". */
export function listWords(items: readonly string[]): string {
    return items.length <= 1 ? items.join("") : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

/** Where a skill plugs in, in one line. */
export const SKILL_PLUGS_IN = "Plugs in from the Skill library, 1 skill per doc type.";

/** The Skill library's route. */
export const SKILLS_PATH = "/oura/skills";

/** The name and page title a new doc gets (B16): what Rename writes too. */
export interface NamePreview {
    /** `(<codes>) <Client>_<Doc>_<Location>_<DD MMMM YYYY>`, no version since #4874. */
    name: string;
    /** Line 1 of the doc: "Answer Plan (S1, S3, B1, G4)". */
    title: string;
    /** Line 2: empty on a new doc (no version, #4874); "Nyonya Table · v1.0" on one made before. */
    versionLine: string;
}

export function namePreview(client: string, docType: string, codes: readonly string[], outlet: string | null, today: string, version: string | null = null, place: string | null = null, month: number | null = null): NamePreview {
    const labels = codeChipsOf(codes, outlet).map((c) => c.label);
    const head = titleHead(docType, place);
    return {
        // The file name takes BARE codes and the outlet as its own Location
        // segment (the team's decision, 6 Oct 2026). Line 1 is unchanged and
        // still carries the outlet on the code itself, "(S2-MK)".
        //
        // The place when we know it, else the outlet code: two outlets' packs
        // must not end up with one name. A doc that is not per outlet has
        // neither and carries no Location. A monthly doc's Location is its plan
        // month, "Month 3", and none without one (ourteam-backend#6868).
        name: docNameOf({ client, docType, codes: Array.from(new Set(codes)), location: isMonthlyType(docType) ? monthPart(month) : place?.trim() || outlet, version, date: today }),
        title: labels.length ? `${head} (${joinCodes(labels)})` : head,
        versionLine: lineTwoOf(client, version),
    };
}

/** The plan month `day` falls in, by the server's rule (`name_month_of`); null without a signed plan. */
export function planMonthOn(board: Pick<BusinessDocuments, "plan"> | null | undefined, day: string): number | null {
    const plan = board?.plan;
    return plan ? termMonth(plan.startedAt, day, plan.termMonths) : null;
}

/** The template's codes that are in this client's contract; all of them when the board is not read. */
export function contractCodes(t: DocTemplate, board: Pick<BusinessDocuments, "codes"> | null): string[] {
    if (!board || board.codes.length === 0) return [...t.codes];
    const sold = new Set(board.codes.filter((r) => r.inContract).map((r) => r.code));
    // No fallback to the whole template (Jack, #3964). The old `in_.length ?
    // in_ : [...t.codes]` meant that a client who bought NONE of a template's
    // codes was offered ALL of them, which is the one case the filter exists
    // for. An empty list is the honest answer: there is nothing here to make.
    return t.codes.filter((c) => sold.has(c));
}

/** The outlets the board already knows for these codes ("MK", "BB"), for quick picks. */
export function knownOutlets(codes: readonly string[], board: Pick<BusinessDocuments, "codes"> | null): string[] {
    if (!board) return [];
    return Array.from(new Set(board.codes.filter((r) => r.outlet && codes.includes(r.code)).map((r) => r.outlet as string)));
}

/**
 * The outlets Create offers in its dropdown: the names the plan holds
 * (engagement.outletNames), or, when the plan holds none, the outlets the
 * board's codes already carry ("MK"). Not both: "Mont Kiara" and "MK" side by
 * side would read as two outlets. The one prefilled stays offered. A name the
 * backend would refuse (outletOk) is left out.
 *
 * `planNames` null is unknown (no engagement, names not confirmed, a failed or
 * pending read): [] then, and the field stays a text box.
 */
export function outletChoices(planNames: readonly { name: string }[] | null | undefined, known: readonly string[], current: string): string[] {
    if (planNames == null) return [];
    const named = planNames.map((o) => o.name.trim()).filter(outletOk);
    const base = named.length > 0 ? named : known.filter(outletOk);
    if (base.length === 0) return [];
    return Array.from(new Set(current && outletOk(current) ? [...base, current] : base));
}

/** Word files in the client's folder: what Convert offers. */
export function wordFiles(board: Pick<BusinessDocuments, "documents"> | null): StaffDocument[] {
    return (board?.documents ?? []).filter((d) => d.fileType === "word");
}

/** A Google Doc link, as Link checks it before asking the server. */
export const GDOC_LINK_RE = /docs\.google\.com\/document\/d\/[A-Za-z0-9_-]{10,}/;

/** "$100 a month, alert at $80": a cap in words. */
export function capText(cap: Pick<SpendCap, "monthCapUsd" | "alertAtUsd">): string {
    return `$${cap.monthCapUsd} a month, alert at $${cap.alertAtUsd}`;
}

/** This month's spend, or "Not measured" until a real run is: never a 0. */
export function spentText(cap: Pick<SpendCap, "spentUsd">): string {
    return cap.spentUsd === null ? "Not measured" : `$${cap.spentUsd.toFixed(2)}`;
}

/** Line 1's words before the codes: "GBP Pack, Mont Kiara" names the outlet, so a client with 2 can tell them apart. */
export function titleHead(docType: string, place: string | null): string {
    return place?.trim() ? `${docType}, ${place.trim()}` : docType;
}

/** The outlet's place name from a doc the board already has for it ("GBP Pack Mont Kiara (S2-MK)" gives "Mont Kiara"); null when none says. */
export function outletPlace(board: Pick<BusinessDocuments, "documents"> | null, outlet: string | null): string | null {
    if (!board || !outlet) return null;
    for (const d of board.documents) {
        if (d.outlet !== outlet) continue;
        const name = merchantDocName(d.pageTitle ?? d.docType ?? "");
        const at = name.indexOf(", ");
        // Only line 1 in Create's own form, "<doc type>, <place>": a converted doc's own title
        // ("Bee Choo Origin, Google Business Profile Optimisation") is not a place (#4307).
        if (at > 0 && d.docType && name.slice(0, at).trim().toLowerCase() === d.docType.toLowerCase()) {
            return name.slice(at + 2).trim() || null;
        }
    }
    return null;
}

/** A doc the client already has for these codes and outlet (any state, internal files aside): Create asks before a second. */
export function sameDocOnBoard(board: Pick<BusinessDocuments, "documents"> | null, docType: string, codes: readonly string[], outlet: string | null, exclude: string | null = null): StaffDocument | null {
    if (!board) return null;
    // The WHOLE identity, not either half of it (Jack, #3964). The `||` made
    // an Answer Plan for S1 the same document as a Monthly Copy Pack for S1,
    // and any older doc of a type the same document whatever its codes — so
    // the confirm could open an unrelated doc, or call a new doc a new version
    // of one it has nothing to do with.
    const wanted = Array.from(new Set(codes));
    return (
        board.documents.find((d) => {
            // in any case: a custom doc type is typed, "treatment menu" is "Treatment Menu" (ourteam-backend#6516)
            if (d.notClientDoc || (d.docType ?? "").toLowerCase() !== docType.toLowerCase() || (d.outlet ?? null) !== outlet) return false;
            // the Word file a convert copies is not a doc already there (#4311)
            if (exclude && d.driveFileId === exclude) return false;
            const has = new Set(d.codes);
            return has.size === wanted.length && wanted.every((c) => has.has(c));
        }) ?? null
    );
}
